class SliderComponent extends HTMLElement {
    constructor() {
        super();
        this.initSlider();
    }

    initSlider() {
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.links = this.slider?.querySelectorAll('a[id^="AbsoluteCardLink-"]');
        this.isDown = false;
        this.isDragging = false;
        this.startX = null;
        this.clickThreshold = 300;
        this.clickStartTime = 0;
        this.scrollLeft;

        if (!this.slider || !this.nextButton) return;

        this.initPages();
        const resizeObserver = new ResizeObserver((entries) => this.initPages());
        resizeObserver.observe(this.slider);

        this.slider.addEventListener('scroll', this.update.bind(this));
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
        // Drag carousel with mouse events
        this.slider.addEventListener('mousedown', this.onMouseDownHandler.bind(this));
        this.slider.addEventListener('mouseleave', this.onMouseLeaveHandler.bind(this));
        this.slider.addEventListener('mouseup', this.onMouseUpHandler.bind(this));
        this.slider.addEventListener('mousemove', this.onMouseMoveHandler.bind(this));
        this.links?.forEach((link) => {
            link.addEventListener('click', this.onLinkClickHandler.bind(this));
        });
    }

    initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
        const forcedToEnable = this.getAttribute('data-always-enabled') !== null ? true : false;
        if (this.sliderItemsToShow.length < 2 && !forcedToEnable) {
            this.prevButton.setAttribute('disabled', 'disabled');
            this.nextButton.setAttribute('disabled', 'disabled');
            return;
        }
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
        this.slidesPerPage =
            Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset) || 1;
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        this.update();
    }

    resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
    }

    update() {
        // Temporarily prevents unneeded updates resulting from variant changes
        // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
        if (!this.slider || !this.nextButton) return;

        const previousPage = this.currentPage;
        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
            this.currentPageElement.textContent = this.currentPage;
            this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
            this.dispatchEvent(
                new CustomEvent('slideChanged', {
                    detail: {
                        currentPage: this.currentPage,
                        currentElement: this.sliderItemsToShow[this.currentPage - 1],
                    },
                }),
            );
        }

        if (this.enableSliderLooping) return;

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
            this.prevButton.setAttribute('disabled', 'disabled');
        } else {
            this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
            this.nextButton.setAttribute('disabled', 'disabled');
        } else {
            this.nextButton.removeAttribute('disabled');
        }
    }

    isSlideVisible(element, offset = 0) {
        const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
        return (
            element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft
        );
    }

    onButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();
        const step = (event.currentTarget.dataset.step || 1) * 2;
        this.slideScrollPosition =
            event.currentTarget.name === 'next'
                ? this.slider.scrollLeft + step * this.sliderItemOffset
                : this.slider.scrollLeft - step * this.sliderItemOffset;
        this.setSlidePosition(this.slideScrollPosition);
    }

    setSlidePosition(position) {
        this.slider.scrollTo({
            left: position,
        });
    }

    onMouseDownHandler(event) {
        this.clickStartTime = Date.now();
        this.isDragging = false;
        this.isDown = true;
        this.slider?.classList.add('active');
        this.startX = event.clientX - this.slider?.offsetLeft;
        this.scrollLeft = this.slider?.scrollLeft;
    }

    onMouseLeaveHandler() {
        if (!this.isDown) return;
        this.isDown = false;
        this.isDragging = false;
        this.links?.forEach((link) => {
            link.setAttribute('draggable', 'true');
        });
        this.slider?.classList.remove('isDragging');
        this.slider?.classList.remove('active');
    }

    onMouseUpHandler() {
        if (!this.isDown) return;
        this.isDown = false;
        this.slider?.classList.remove('active');

        const clickDuration = Date.now() - this.clickStartTime;
        this.isDragging = clickDuration > this.clickThreshold;

        // if (this.isDragging) {
        //     this.isDragging = false;
        // }

        this.slider?.classList.remove('isDragging');
        this.links?.forEach((link) => {
            link.setAttribute('draggable', 'true');
        });
        this.slider?.classList.remove('active');

        if (this.enableSliderLooping) return;

        // Update button states based on the current scroll position
        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
            this.prevButton.setAttribute('disabled', 'disabled');
        } else {
            this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
            this.nextButton.setAttribute('disabled', 'disabled');
        } else {
            this.nextButton.removeAttribute('disabled');
        }
    }

    onMouseMoveHandler(event) {
        if (!this.isDown) return;
        event.preventDefault();
        this.isDragging = true;

        this.slider?.classList.add('isDragging');
        this.links?.forEach((link) => {
            link.setAttribute('draggable', 'false');
        });
        const x = event.pageX - this.slider.offsetLeft;
        const walk = (x - this.startX) * 3;

        this.slider.scrollLeft = this.scrollLeft - walk;
    }
    // The Links are setted to not draggable, in this way on mouseup on the links, we navigate to the page.
    // If we are dragging, we prevent the navigation e remove the dragging flag here, because this is the last fired event
    onLinkClickHandler(event) {
        if (!this.isDragging) return;
        event.preventDefault();
        this.isDragging = false;
    }
}

customElements.define('slider-component', SliderComponent);
